import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Mail, Phone, Lock, Eye, EyeOff, UserPlus } from 'lucide-react';
import { toast } from 'react-hot-toast';
import CustomInput from '../../../../../components/forms/CustomInput';
import RoleSelectionCards from './RoleSelectionCards';
import FeatureLocked from '../../../../../components/gating/FeatureLocked';

const AddUserForm = ({ isRolesLocked, isSubadmin, onAddMember }) => {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [role, setRole] = useState(isSubadmin ? 'bookings' : 'subadmin');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isRolesLocked) {
      toast.error('Role & Team Management requires the Enterprise Plan.');
      return;
    }
    if (!email || !phone || !password || !role) {
      toast.error('Please fill in all required fields');
      return;
    }

    if (password.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }

    setLoading(true);
    try {
      const res = await onAddMember({
        email,
        phone_number: phone,
        password,
        role
      });

      if (res?.success) {
        toast.success(`Team member created with role: ${role.toUpperCase()}`);
        navigate('/role-management');
      }
    } catch (err) {
      toast.error(err.message || 'Failed to create team member');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="relative">
      <div className="bg-white rounded-3xl border border-gray-100 shadow-sm p-6 md:p-8">
        <form onSubmit={handleSubmit} className="space-y-6 max-w-3xl">
          {/* Field 1: Email */}
          <CustomInput
            label="Email Address *"
            type="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="staff@trrip.live"
            icon={Mail}
          />

          {/* Field 2: Phone Number */}
          <CustomInput
            label="Phone Number *"
            type="tel"
            required
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            placeholder="9876543210"
            icon={Phone}
          />

          {/* Field 3: Password */}
          <div className="relative">
            <CustomInput
              label="Temporary Password *"
              type={showPassword ? 'text' : 'password'}
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="••••••••"
              icon={Lock}
              inputClassName="pr-10"
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="absolute right-3.5 top-[32px] text-gray-400 hover:text-gray-600 focus:outline-none cursor-pointer"
            >
              {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
            </button>
          </div>

          {/* Field 4: Role Selection */}
          <RoleSelectionCards
            role={role}
            setRole={setRole}
            isSubadmin={isSubadmin}
          />

          {/* Form Action Buttons */}
          <div className="flex items-center justify-end gap-3 pt-6 border-t border-gray-100">
            <button
              type="button"
              onClick={() => navigate('/role-management')}
              className="px-6 py-3.5 border border-gray-200 text-gray-700 font-bold text-sm rounded-2xl hover:bg-gray-50 transition-colors cursor-pointer"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="flex-1 py-3.5 bg-[#067978] hover:bg-[#05605f] text-white font-bold text-sm rounded-2xl shadow-lg shadow-teal-900/20 transition-all flex items-center justify-center gap-2 active:scale-[0.99] cursor-pointer disabled:opacity-70 disabled:cursor-not-allowed"
            >
              <UserPlus size={18} />
              <span>{loading ? 'Adding Member...' : 'Add Team Member'}</span>
            </button>
          </div>
        </form>
      </div>

      {isRolesLocked && (
        <FeatureLocked featureKey="role-management" overlay={true} />
      )}
    </div>
  );
};

export default AddUserForm;
